/* Homepage > Get started > Any employment status except student > Location screen */

import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Amplitude } from '@amplitude/react-amplitude';
import MetaTags from 'react-meta-tags';

import layout from '../../config/layout';
import { validatePostcode } from '../../utils/validation';
import { useStateValue } from "../../services/state";
import { cleanString } from '../../utils/helper';
import { createSubmitData } from '../../utils/helper';
import { useHistory } from 'react-router-dom';
import { useGetScreenData } from '../../hooks/getDataHooks';
import { useRouteMatch } from 'react-router-dom';

import ThreeDotsBgLayout from '../../ui/layout/ThreeDotsBgLayout';
import { Col, Row } from 'react-styled-flexboxgrid';
import Input from '../../ui/form/Input';
import { PrimaryButton, GrayButton } from '../../ui/buttons/Buttons';
import Multiselect from '../../components/multiselect/Multiselect';
import { Label } from '../../ui/typography/Typography';
import { Error } from '../../ui/typography/Typography';
import Tooltip from '../../components/Tooltip';
import RestartFlow from '../RestartFlow';
import LoadingScreen from '../LoadingScreen';

const LocationScreen = () => {
  const [ generalError, setGeneralError ] = useState();
  const [ postcode, setPostcode ] = useState('');
  const [ postcodeError, setPostcodeError ] = useState(null);
  const [ relocate, setRelocate ] = useState([
    { name: 'Yes', selected: false, id: 0, value: true },
    { name: 'No', selected: false, id: 1, value: false }
  ]);
  const [isLoading, setIsLoading] = useState([]);
  const [ loadingText, setLoadingText ] = useState();
  const history = useHistory();
  const [{ user_id, loading, context }, dispatch] = useStateValue();
  const match = useRouteMatch("/:userId/location");

  const valueFormatting = (value) => value.toUpperCase();

  useGetScreenData('location', setIsLoading, match.params.userId);

  const handleRelocateChange = (id, data) => {
    const values = [...data];
    // eslint-disable-next-line no-unused-vars
    for (const value of values) {
      if (value.id === id) {
        value.selected = !value.selected;
      } else {
        value.selected = false;
      }
    }
    setRelocate(values)
  };

  const checkPostcode = e => {
    // Postcode validity
    const value = e.target.value;
    const error = validatePostcode(cleanString(value)) ? null : 'This is not a valid UK postcode';
    setPostcodeError(value === "" ? null : error);
  };

  const validateForm = () => {
    let isValid = true;
    if (!validatePostcode(cleanString(postcode))) isValid = false;
    if (context.age >= 24 && !relocate.find(item => item.selected === true)) isValid = false;
    return isValid;
  };

  const submitForm = (logEvent) => {
    if (validateForm()) {
      if (context.age < 22) {
        setLoadingText(context.loading_message);
      }
      setIsLoading(true);

      const formData = createSubmitData({
        location_details: {
          postcode: cleanString(postcode),
          open_to_relocating: (context.age >= 24 ? relocate.find(a => a.selected === true).value : null),
        },
        user_id: user_id,
      });
      axios.post('/api/v1/handleLocationInput', formData).then((res) => {
        if (res.data.errors) {
          setIsLoading(false);
          // eslint-disable-next-line no-unused-vars
          for (const error of res.data.errors) {
            if (error.hasOwnProperty('field-errors')) {
              // eslint-disable-next-line no-unused-vars
              for (const item of error['field-errors']) {
                if (item.hasOwnProperty('postcode')) {
                  const postcodeErr = item.postcode.reduce((i, err) => (err + ''), '');
                  setPostcodeError(postcodeErr);
                }
              }
            } else {
              setGeneralError(error['non-field-errors']);
            }
          }
        } else {
          dispatch({type: 'updateResponse', data: res.data});
          logEvent('Completed Location Input', {
            postcode:  cleanString(postcode),
            open_to_relocating: (context.age >= 24 ? relocate.find(a => a.selected === true).value : null),
          });
          if (res.data.screen.context.question_number) {
            dispatch({type: 'update', values: { window_top: 0, showPreQuizInfoScreen: true }});
            setTimeout(() => {
              setIsLoading(false);
              history.push(`/${user_id}/${res.data.screen.name}/${res.data.screen.context.question_number}`);
            }, 2500);
          } else {
            setIsLoading(false);
            history.push(`/${user_id}/${res.data.screen.name}`);
          }
        }
      })
    }
  };

  if (loading || isLoading) return <LoadingScreen text={loadingText} />

  return (
    <>
      <MetaTags>
        <title>Location</title>
        <meta id="meta-description" name="description" content="Input your location" />
      </MetaTags>
      <ThreeDotsBgLayout
        title="Your location"
        subtitle="This will help us find jobs, courses and services near you"
      >
        <Row>
          <Col xs={12} md={8} mdOffset={2}>
            <QuestionContainer>
              <InputWrap>
                <Input
                  onBlur={checkPostcode}
                  type="text"
                  label="What's your postcode?"
                  handleChange={value => {setPostcode(value.toUpperCase()); setPostcodeError(null)}}
                  valueFormatting={valueFormatting}
                  error={postcodeError}
                  placeholder="SW1A 2AA"
                  testid="postcode"
                />
              </InputWrap>
              {context.age >= 24 && (
                <InputWrap>
                  <Label>Are you open to relocating?</Label>
                  <Multiselect
                    values={relocate}
                    handleChange={handleRelocateChange}
                    name=""
                    testid="relocate"
                  />
                </InputWrap>
              )}
              <ButtonWrap>
                {validateForm() ? (
                  <Amplitude userProperties={{
                    postcode: postcode,
                    open_to_relocating: (context.age >= 24 ? relocate.find(a => a.selected === true).value : null),
                  }}>
                    {({ logEvent }) => (
                      <PrimaryButton onClick={() => submitForm(logEvent)} data-testid="next">Next</PrimaryButton>
                    )}
                  </Amplitude>
                ) : (
                  <Tooltip text="Please enter your location details above">
                    <GrayButton disabled>Next</GrayButton>
                  </Tooltip>
                )}
                {generalError && <Error>{generalError.map(item => item)}</Error>}
              </ButtonWrap>

            </QuestionContainer>
          </Col>
        </Row>
        <RestartFlow largeMarginTop endpoint={context.endpoint} />
      </ThreeDotsBgLayout>
    </>
  );
};

export default LocationScreen;

const QuestionContainer = styled.div`
  margin: auto;
  max-width: 352px;
`;

const InputWrap = styled.div`
  margin-bottom: 20px;
`;

const ButtonWrap = styled.div`
  margin-top: 52px;
  text-align: center;

  @media(max-width: ${layout.breakpoints.MD}) {
    margin-top: 32px;
  }
`;
